/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {ReactNode, useState} from "react";
import {FileProcessingMessages, IMessage} from "../../../pages/radar/SensorRadar/FileProcessingMessages";
import Button from "react-bootstrap/Button";
import {useWizard} from "react-use-wizard";
import {
  useSelectAllErrorMessages, useSelectFileDataHasErrors, 
  useSelectFileDataHasWarnings,
  useSelectFileUploadHasErrors,
  useSelectFileUploadResponse,
  useSelectAllWarningMessages,
  useSliceActions
} from "../../../../redux/ducks/Wizard/SliceProvider";
import {useDispatch} from "react-redux";
import {SubmitWizardPopup} from "../SubmitWizardPopup";
import {isMultidimensionalArrayEmpty} from "../helpers";
import ArgosButton from "../../Buttons/ArgosButton";

interface IProps {
  content1: ReactNode | null
  content2: ReactNode
  statementMessages?: IMessage[] | undefined
  errorMessages: IMessage[]
  warningMessages?: IMessage[] | undefined
  messagesTitle: string
  isFirstWizardStep: boolean
  isLastWizardStep: boolean
  customTopRow: ReactNode | null;
  isSubmitDisabled?: () => boolean
  handleSubmit?: () => void
  submitPopupEnabled?: boolean
  isFileUploading?: boolean
  popupTitle?: string
  submitDialogContentText?: string
  id?: string
}

const TemplateStep = (props: IProps) => {
  const {
    statementMessages,
    errorMessages,
    warningMessages,
    messagesTitle,
    content1,
    content2,
    isFileUploading,
    isFirstWizardStep,
    isLastWizardStep,
    handleSubmit
  } = props
  const {previousStep, nextStep} = useWizard();
  const responseData = useSelectFileUploadResponse()
  const dispatch = useDispatch()
  const {setWizardOpen, clearState} = useSliceActions()
  const [popBool, setPopBool] = useState(false)
  const didFileUploadFail = useSelectFileUploadHasErrors()
  const sheetErrors = !isMultidimensionalArrayEmpty(useSelectAllErrorMessages())
  const fileDataHasErrors = useSelectFileDataHasErrors()
  const fileDataHasWarnings = useSelectFileDataHasWarnings()

  const handleCloseDialog = () => {
    dispatch(setWizardOpen(false))
    dispatch(clearState())
  }
  const nextOrFinishButton = !isLastWizardStep ?
    (!isFileUploading && !didFileUploadFail &&
      <ArgosButton
        onClick={() => nextStep()}>
        Next
      </ArgosButton>
    ) :
    (
      <ArgosButton
        disabled={false}
        onClick={() => {
          setPopBool(true)
        }}
      >
        Finish
      </ArgosButton>
    )
  return (
    <div style={{display: 'flex', flexFlow: 'column nowrap', height: '100%', gap: '20px'}}>
      <div style={{flex: '1 1 auto', overflow: 'auto', display: 'flex', flexFlow: 'column nowrap'}}>
        <div style={{display: 'flex', overflow: 'auto'}}>
          <div style={{
            overflow: 'auto', flex: '0 0 10%', display: 'flex', justifyContent: 'center', alignItems: 'center'
          }}>
            {content1}
          </div>
          <div style={{
            flex: '1 1 auto',
            height: '100%',
            overflow: 'auto',
            border: isFirstWizardStep ? 'none' : '2px solid black',
            display: 'flex',
            flexFlow: 'column',
            padding: 10
          }}>
            {content2}
          </div>
        </div>
      </div>

      <div style={{flex: '0 0 auto', display: 'flex', flexFlow: 'column nowrap'}}>
        <div style={{display: 'flex'}}>
          <div style={{
            flex: '0 0 10%', display: 'flex', flexFlow: 'column nowrap', alignItems: 'center',
            justifyContent: 'flex-end', gap: '20px'
          }}>
            {responseData && !didFileUploadFail && nextOrFinishButton}

            {!isFileUploading &&
              <ArgosButton
                onClick={() => previousStep()}
                style={{visibility: isFirstWizardStep ? 'hidden' : 'initial'}}
              >
                Previous
              </ArgosButton>
            }
            <ArgosButton
              onClick={() => handleCloseDialog()}
            >
              Exit
            </ArgosButton>
          </div>
          <div style={{flex: '1 1 auto', alignSelf: 'stretch', overflow: 'hidden'}}>
            <FileProcessingMessages statementMessages={statementMessages} errorMessages={errorMessages} warningMessages={warningMessages} title={messagesTitle} enumerate={!isFirstWizardStep}/>
          </div>
        </div>
      </div>

      {
        (props.submitPopupEnabled && props.handleSubmit) &&
        <SubmitWizardPopup
          popBool={popBool} setPopBool={setPopBool}
          handleSubmit={handleSubmit!!}
          didFileUploadFail={didFileUploadFail} sheetErrors={sheetErrors} fileDataHasErrors={fileDataHasErrors}
          title={props?.popupTitle ?? ''} contentText={props?.submitDialogContentText}
        />
      }

    </div>
  )
}

export default TemplateStep